import { useCallback, useMemo } from "react";

import { leadsCostTypeMapper } from "app/pages/sales-intelligence/modules/leads-search/utils/quota";
import { useExportQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useExportQuota";
import { useDataCreditsQuery } from "app/pages/sales-intelligence/modules/quota/queries/useDataCreditsQuery";
import type { ExportQuotaState } from "app/pages/sales-intelligence/modules/quota/types";
import {
    getDataPointCost,
    calculateTotalDataPointsCost,
} from "app/pages/sales-intelligence/modules/quota/utils/dataCredits";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import solutionUrlsService from "app/pages/sales-intelligence/services/solution-urls";

import type { ConfigField, ConfigType } from "../types/columns";

type HookParams = {
    selectedFields: readonly ConfigField[];
    matchedRecordsCount: number;
    basicPrice?: number;
};

type HookResult = {
    isDataCreditsEnabled: boolean;
    isLoading: boolean;
    quota: ExportQuotaState["data"];
    totalCost: number;
    recordCost: number;
    isOutOfQuota: boolean;
    dataCreditsLinkUrl: string;
    getFieldCost: (fieldType: ConfigType) => number | null;
};

/**
 * Hook that provides all quota state for CSV enrichment
 */
export const useCsvEnrichmentQuota = ({
    selectedFields,
    matchedRecordsCount,
    basicPrice = 0,
}: HookParams): HookResult => {
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const { data: dataCreditsData, isLoading: isDataCreditsLoading } = useDataCreditsQuery({
        enabled: isDataCreditsEnabled,
        select: ({ dataPoints }) => dataPoints,
    });

    const { data: exportQuota, isLoading: isExportQuotaLoading } = useExportQuota();

    const isLoading = isDataCreditsLoading || isExportQuotaLoading;

    const getFieldCost = useCallback(
        (fieldType: ConfigType): number | null => {
            if (!isDataCreditsEnabled || !dataCreditsData) {
                return null;
            }

            return getDataPointCost(dataCreditsData, fieldType, leadsCostTypeMapper);
        },
        [isDataCreditsEnabled, dataCreditsData],
    );

    const recordCost = useMemo(() => {
        if (!isDataCreditsEnabled || !dataCreditsData || (selectedFields.length === 0 && basicPrice === 0)) {
            return 0;
        }

        const fieldTypes = selectedFields.map(({ type }) => type);

        return basicPrice + (calculateTotalDataPointsCost(dataCreditsData, fieldTypes, leadsCostTypeMapper) ?? 0);
    }, [isDataCreditsEnabled, dataCreditsData, selectedFields]);

    const totalCost = recordCost * matchedRecordsCount;

    const isOutOfQuota = isDataCreditsEnabled
        ? totalCost > exportQuota.remaining // Data credits mode: cost vs credits
        : matchedRecordsCount > exportQuota.remaining; // Legacy mode: rows vs quota

    return {
        isDataCreditsEnabled: isDataCreditsEnabled,
        isLoading,
        quota: exportQuota,
        totalCost,
        recordCost,
        isOutOfQuota,
        getFieldCost,
        dataCreditsLinkUrl: solutionUrlsService.getAccountDataCreditsUrl(),
    };
};
